import {
  addDays,
  addHours,
  addMilliseconds,
  addMinutes,
  addMonths,
  addQuarters,
  addSeconds,
  addWeeks,
  addYears,
  isValid,
  parseISO,
  startOfDay,
  startOfHour,
  startOfMinute,
  startOfMonth,
  startOfQuarter,
  startOfSecond,
  startOfWeek,
  startOfYear,
  subDays,
  subHours,
  subMilliseconds,
  subMinutes,
  subMonths,
  subQuarters,
  subSeconds,
  subWeeks,
  subYears,
} from 'date-fns';
import _isString from 'lodash-es/isString';

type DateOperatorDimensions =
  | 'y'
  | 'Q'
  | 'M'
  | 'w'
  | 'd'
  | 'h'
  | 'm'
  | 's'
  | 'ms';
type DateOperatorFunction = (date: Date | number, amount: number) => Date;
type DateOperatorsDict = {
  [key in DateOperatorDimensions]: DateOperatorFunction;
};

const add: DateOperatorsDict = {
  y: addYears,
  Q: addQuarters,
  M: addMonths,
  w: addWeeks,
  d: addDays,
  h: addHours,
  m: addMinutes,
  s: addSeconds,
  ms: addMilliseconds,
};

const subtract: DateOperatorsDict = {
  y: subYears,
  Q: subQuarters,
  M: subMonths,
  w: subWeeks,
  d: subDays,
  h: subHours,
  m: subMinutes,
  s: subSeconds,
  ms: subMilliseconds,
};

type DateStartOfDimensions = 'y' | 'Q' | 'M' | 'w' | 'd' | 'h' | 'm' | 's';
type DateStartOfFunction = (date: Date | number) => Date;
type DateStartOfDict = { [key in DateStartOfDimensions]: DateStartOfFunction };

const startOf: DateStartOfDict = {
  y: startOfYear,
  Q: startOfQuarter,
  M: startOfMonth,
  w: startOfWeek,
  d: startOfDay,
  h: startOfHour,
  m: startOfMinute,
  s: startOfSecond,
};

const isStartOfDimension = (value: string): value is DateStartOfDimensions =>
  value in startOf;

const isOperatorDimension = (value: string): value is DateOperatorDimensions =>
  value in add;

function applyOperator(date: Date, op: string): Date {
  if (op[0] === '/') {
    const operator = op[1];
    return isStartOfDimension(operator) ? startOf[operator](date) : date;
  }
  /* cspell:disable-next-line */
  const durationRegex = /(\d+)([yMwdhms])/g;
  let match = null;
  while ((match = durationRegex.exec(op)) !== null) {
    const value = match[1];
    const key = match[2];
    if (!isOperatorDimension(key)) {
      continue;
    }
    if (op[0] === '+') {
      return add[key](date, Number.parseInt(value, 10));
    }
    if (op[0] === '-') {
      return subtract[key](date, Number.parseInt(value, 10));
    }
  }
  return date;
}

function applyExpr(date: Date, expr: string): Date {
  let dateUpdate = date;
  /* cspell:disable-next-line */
  const operatorRegex = /(?:[+-](?:\d+[yMwdhms])+|\/[yMwdhms])/g;
  let match = null;
  while ((match = operatorRegex.exec(expr)) !== null) {
    dateUpdate = applyOperator(dateUpdate, match[0]);
  }
  return dateUpdate;
}

function relativeTimeToDate(time?: string | number | Date): Date | number {
  let expr = time;
  if (!_isString(expr)) {
    return 0;
  }

  if (expr === 'now') {
    return new Date();
  }

  expr = expr.replace(/now\//g, 'now-0d/');

  const regex =
    /* cspell:disable-next-line */
    /(?:\|\|)?\s*(?:\[(@|Z|[+-]\d\d:\d\d)])?((?:\/[yMwdhms])?\s*(?:[+-]\s*(?:\d+\s*[yMwdhms]\s*)+(?:\/[yMwdhms])?\s*)+)$/;
  const match = regex.exec(expr);

  if (!match) {
    return 0;
  }

  const index = match.index;
  const datePart = expr.slice(0, index);

  const date =
    datePart === '' || datePart.toLowerCase() === 'now'
      ? new Date()
      : parseISO(datePart);

  if (!isValid(date)) {
    return 0;
  }

  // set the timezone (utcOffset), if any
  // the feature is broken (utcOffset is not a part of Date),
  // seems more like moment remnants
  // if (match[1]) {
  //   if (match[1] === '@') {
  //     date.utcOffset(datePart);
  //   } else {
  //     date.utcOffset(match[1]);
  //   }
  // }

  //  get expression part and strip whitespaces, then apply
  return applyExpr(date, match[0].replace(/(\s+|\|\|)/g, ''));
}

export default relativeTimeToDate;
